import { useEffect, useEffectEvent, useState } from 'react';

import { Navigate, useLocation, useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import {
  IconCertificate,
  IconEdit,
  IconFilter,
  IconPrinter,
  IconUserPlus,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import uniq from 'lodash/uniq';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { TextFieldType } from '@cfa/react-components';
import { MessageBlock } from '@cfa/react-core';
import { Show } from '@cfa/system-icons';

import useAuth from '@/hooks/useAuth';
import useGetStatusLabels from '@/hooks/useGetStatusLabels';
import { usePlanLocations } from '@/hooks/usePlanLocations';
import { useSortOptions } from '@/hooks/useSortOptions';

import { getFilterableStatus } from '@/utils/getFilterableStatus';
import { isValidPlanId as checkIsValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';
import { isApiError } from '@/utils/request';
import { cn } from '@/utils/tailwind';
import { getTimeSpentOnPlan } from '@/utils/time';
import { showAssignmentSuccessToast } from '@/utils/training-plans/show-assignment-success-toast';
import { withRoles } from '@/utils/withRoles';

import GenericError from '@/components/Error/GenericError';
import Filter, {
  FilterCheckboxSectionProps,
  FilterDesktop,
  FilterMobile,
  FilterSearchBarProps,
} from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import { Header } from '@/components/header';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import { PopoverMenu } from '@/components/popover-menu';
import AddTeamMembersPopUp from '@/components/popups/AddTeamMemberPopUp/AddTeamMembersPopUp';
import DueDatePopUp from '@/components/popups/DueDatePopUp';
import { printPlan } from '@/components/print-report';
import {
  PrintCertificateModals,
  usePrintCertificates,
} from '@/components/PrintCertificates';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import { queryClient } from '@/services';
import { processUserName } from '@/services/modelHelper';

import AddTeamMemberFab from './AddTeamMemberFab';
import EnterTrainTogether from './Modals/EnterTrainTogether';
import PlanInUseModal from './Modals/PlanInUseModal';
import TrainTogether from './Modals/TrainTogether';
import UnassignTeamMember from './Modals/UnassignTeamMember';
import TeamMembers from './TeamMembers/TeamMembers';

import {
  assignUsersToChecklist2,
  listAssignedChecklistsQueryKey,
} from '@/_generated/api-clients/content';
import {
  getActivePlanAssignmentsQueryKey,
  getManagedPlansQueryKey,
  getPlanAndAssignmentsOptions,
  getPlanAndAssignmentsQueryKey,
  getPlanReportOptions,
  getUsersForLocationsOptions,
  listEnrollmentsByCourseOptions,
  PathwayUser,
  updateDueDate,
} from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { filtersToFilterInValues } from '@/atoms/data-view/helpers';
import {
  trainingModeAtom,
  trainingModeDispatchAtom,
} from '@/atoms/trainingMode';
import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';
import {
  managePlansAtom,
  toggleRefetchDispatchAtom,
} from '@/routes/TrainingPlans/ManagePlans/state';

const trainingModeFocusAtom = focusAtom(trainingModeAtom, o =>
  o.pick(['isTrainingModeOpen', 'hasTeamMembersAssignedToPlan']),
);

const activePlanIdAtom = focusAtom(trainingModeAtom, o =>
  o.prop('activePlanId'),
);

const managePlansFocusAtom = focusAtom(managePlansAtom, o =>
  o.pick([
    'shouldRefetchAssignedChecklist',
    'shouldRefetchAssignedCompliance',
    'shouldRefetchAssignedTeamMembers',
    'shouldRefetchCourseReport',
  ]),
);

const showTrainTogetherPopupAtom = focusAtom(trainingModeAtom, o =>
  o.prop('showTrainTogetherPopup'),
);

const deriveMemberFields = (
  user: Parameters<typeof processUserName>[0],
  status: string,
  dueDate?: string | null,
) => ({
  name: processUserName(user),
  simpleUserName: processUserName({
    prefName: user?.prefName,
    firstName: user?.firstName,
    lastName: user?.lastName,
  }),
  isComplete:
    status === Constants.TRAINING_PLANS.COMPLETED ||
    status === Constants.LEARN_UPON_TRAINING_PLANS.COMPLETED ||
    status === Constants.LEARN_UPON_TRAINING_PLANS.PASSED,
  isFailed: status === Constants.LEARN_UPON_TRAINING_PLANS.FAILED,
  selectedDueDate: new Date(dueDate ?? Infinity).getTime(),
});

export type TeamMemberRow = ReturnType<typeof deriveMemberFields> & {
  id: string;
  filterStatus: string;
  previouslyCompletedPlanAssignmentDate: string;
  timeSpentOnPlan: string | null;
  percentComplete: number;
  completedStepCount: number;
  completionDate?: string | null;
  dueDate?: string | null;
  expirationDate?: string;
  finalScore?: number;
  locations?: string[];
  status?: string;
  userId?: string;
};

interface TeamMemberListProps {
  /** Is the view for reports route? @default false */
  reportsRoute?: boolean;
}

const PlanAssignees: React.FC<TeamMemberListProps> = ({
  reportsRoute = false,
}) => {
  const dataViewKey = reportsRoute
    ? DataViewAtomFamilyKey.REPORTS_PLAN_TEAM_MEMBERS
    : DataViewAtomFamilyKey.MANAGE_TEAM_MEMBERS;

  const dataSource = sourceDataFamily(dataViewKey);

  const sortKey = reportsRoute
    ? { reportsPlansPlanView: true }
    : { managePlanView: true };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const track = useAmplitudeTrack();
  const statusLabels = useGetStatusLabels(dataViewKey);

  const location = useLocation();
  const { search } = location;
  const state = z
    .object({ refetch: z.boolean().optional() })
    .nullable()
    .catch(null)
    .parse(location.state as unknown);

  const { planId } = useParams<{ planId: string }>();
  const safePlanId = planId ?? '';

  const isLearnUpon = search.includes('compliancePlan');

  const sortOptions = useSortOptions(sortKey);

  const isLearnUponFoodSafety =
    decodeURI(search).includes(Constants.FOOD_SAFETY_COURSE_NAME) &&
    search.includes('compliancePlan');

  const [
    {
      display: { total, items, showing },
      filters,
      sort,
    },
    {
      resetFilters,
      setSort,
      toggleCheckboxFilter,
      resetAllCheckboxFilters,
      setSearch,
      resetSearch,
    },
  ] = useDataView<TeamMemberRow>(dataViewKey);

  const currentFilters = filtersToFilterInValues(filters);

  const { data: auth } = useAuth();
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  // Certificate button hook
  const certificateHook = usePrintCertificates({ planId: safePlanId });

  const {
    isTrainingModeOpen: trainingModeModalIsOpen,
    hasTeamMembersAssignedToPlan,
  } = useAtomValue(trainingModeFocusAtom);

  const toggle = useSetAtom(toggleRefetchDispatchAtom);
  const s = useAtomValue(managePlansFocusAtom);

  const patchTrainingMode = useSetAtom(trainingModeAtom);

  const setActivePlanId = useSetAtom(activePlanIdAtom);
  const sendTrainingModeAction = useSetAtom(trainingModeDispatchAtom);
  const showTrainTogetherPopup = useAtomValue(showTrainTogetherPopupAtom);
  const setTeamMembers = useSetAtom(sourceDataFamily('reports-team-members'));

  const { assignTeamMembers } = useAssignTeamMembers();

  const shouldIRefetchAssignedCompliance = s.shouldRefetchAssignedCompliance;
  const shouldIRefetchAssignedTeamMembers = s.shouldRefetchAssignedTeamMembers;
  const [showAddTeamMembersMenu, setShowAddTeamMembersMenu] = useState(false);
  const [showAddTeamMembersPopup, setShowAddTeamMembersPopup] = useState(false);
  const [showDueDatePopup, setShowDueDatePopup] = useState(false);
  const [showUnassignTeamMemberPopup, setShowUnassignTeamMemberPopup] =
    useState(false);
  const [showSelectLanguagePopup, setShowSelectLanguagePopup] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [activeTeamMember, setActiveTeamMember] = useState({
    id: '',
    name: '',
  });
  const [temporaryTeamMembers, setTemporaryTeamMembers] = useState<
    PathwayUser[]
  >([]);
  const [isDueDateUpdate, setIsDueDateUpdate] = useState(false);
  const [showPlanInUseModal, setShowPlanInUseModal] = useState(false);
  const [filterAndSortButtonIsOpen, setFilterAndSortButtonIsOpen] =
    useState(false);

  const setDataSource = useSetAtom(dataSource);

  const isPlanQueryEnabled = !!planId && !isLearnUpon && !reportsRoute;
  const isEnrollmentsQueryEnabled = !!planId && isLearnUpon;
  const isReportPlanQueryEnabled = !!planId && !isLearnUpon && reportsRoute;
  // @TODO: Migrate off browser-router for route-level validation

  const isValidPlanId = checkIsValidPlanId(planId);

  const [
    {
      isFetching: isFetchingPlanAndAssignments,
      isPending: isPendingPlanAndAssignments,
      error: errorGettingPlan,
      refetch: _refetchPlanAndAssignments,
      data: planAndAssignments,
    },
    {
      error: errorGettingUsers,
      isFetching: isFetchingUsers,
      isPending: isPendingUsers,
      data: users,
    },
    {
      data: enrollments,
      refetch: refetchAssignedComplianceTeamMembers,
      error: errorGettingComplianceStatus,
    },
    {
      data: planReport,
      isFetching: isFetchingPlanReport,
      isPending: isPendingPlanReport,
    },
  ] = useQueries({
    queries: [
      {
        ...getPlanAndAssignmentsOptions({ path: { planId: safePlanId } }),
        enabled: isPlanQueryEnabled && isValidPlanId,
      },
      {
        ...getUsersForLocationsOptions({
          query: { locationNumbers: locationsWithAtLeastTrainer },
        }),
      },
      {
        ...listEnrollmentsByCourseOptions({
          path: { courseId: safePlanId },
          query: {
            'List of locations to filter':
              locationsWithAtLeastTrainer.join(','),
          },
        }),
        enabled: isEnrollmentsQueryEnabled,
      },
      {
        ...getPlanReportOptions({ path: { planId: safePlanId } }),
        enabled: isReportPlanQueryEnabled && isValidPlanId,
      },
    ],
  });

  const isCompliance =
    isLearnUpon ||
    planAndAssignments?.plan?.type === 'COMPLIANCE' ||
    planAndAssignments?.plan?.type === 'ANCHOR';

  // Guard with isPlanQueryEnabled: usePlansCertificatesData also calls
  // getPlanAndAssignmentsOptions, populating the cache even when this query is
  // disabled (e.g. LearnUpon compliance plans). TanStack Query serves cached
  // data to disabled queries, so without this guard planAndAssignments would be
  // non-null and leak XP plan data into the Add Team Members popup, causing it
  // to treat all members as already assigned.
  const activePlan = isPlanQueryEnabled ? planAndAssignments : undefined;

  const planName = activePlan?.plan?.name ?? planReport?.name;
  const isTrainTogetherEnabled =
    hasTeamMembersAssignedToPlan &&
    activePlan?.plan?.permissions?.trainTogether;

  // types don't have updateDueDate, so need to parse from permissions
  const userAllowedToUpdateTMDueDateOnPlan =
    z
      .object({
        updateDueDate: z.boolean(),
      })
      .safeParse(activePlan?.plan?.permissions).data?.updateDueDate ?? false;

  const refetchPlanAndAssignments = useEffectEvent(() => {
    if (!isValidPlanId) {
      throw new Error('Invalid planId');
    }
    return _refetchPlanAndAssignments();
  });

  const isFetching =
    (isPlanQueryEnabled &&
      (isFetchingPlanAndAssignments || isPendingPlanAndAssignments)) ||
    (isReportPlanQueryEnabled &&
      (isFetchingPlanReport || isPendingPlanReport)) ||
    isFetchingUsers ||
    isPendingUsers;

  useEffect(() => {
    if (planReport) {
      const totalSteps = planReport.totalSteps ?? 0;
      setDataSource(
        (planReport.planUserReports ?? [])
          .filter(r => r.status !== Constants.TRAINING_PLANS.UNASSIGNED)
          .map(({ user, ...report }) => {
            const status = report.status ?? '';
            return {
              ...deriveMemberFields(user, status, report.dueDate),
              id: user?.id ?? '',
              userId: user?.id,
              completionDate: report.completionDate,
              dueDate: report.dueDate,
              status,
              filterStatus: getFilterableStatus(status, false),
              previouslyCompletedPlanAssignmentDate:
                report.latestCompletionReport?.completionDate ??
                report.completionDate ??
                '',
              timeSpentOnPlan: getTimeSpentOnPlan({
                timeTaken: report.timeTakenInSeconds,
              }),
              percentComplete: totalSteps
                ? Math.round(((report.stepsComplete ?? 0) / totalSteps) * 100)
                : 0,
              completedStepCount: report.stepsComplete ?? 0,
            };
          }),
      );
      return;
    }

    if (users && activePlan) {
      const usersById = new Map(users.map(u => [u.adId, u]));
      const totalSteps = activePlan.plan?.totalSteps ?? 0;
      setDataSource(
        (activePlan.assignments ?? [])
          .filter(a => a.status !== Constants.TRAINING_PLANS.UNASSIGNED)
          .map(assignment => {
            const user = usersById.get(assignment.userId);
            const status = assignment.status ?? '';
            return {
              ...deriveMemberFields(user, status, assignment.dueDate),
              id: assignment.userId ?? assignment.id ?? '',
              completionDate: assignment.completionDate,
              dueDate: assignment.dueDate,
              status,
              locations: user?.locationList,
              filterStatus: status
                ? getFilterableStatus(status, false)
                : 'not_completed',
              previouslyCompletedPlanAssignmentDate:
                assignment.latestCompletionReport?.completionDate ??
                assignment.completionDate ??
                '',
              timeSpentOnPlan: getTimeSpentOnPlan({
                steps: assignment.steps,
              }),
              percentComplete: totalSteps
                ? Math.round(
                    ((assignment.stepsComplete ?? 0) / totalSteps) * 100,
                  )
                : 0,
              completedStepCount: assignment.stepsComplete ?? 0,
            };
          }),
      );
      return;
    }

    if (users && enrollments) {
      const seen = new Set<string>();
      setDataSource(
        users.reduce<TeamMemberRow[]>((rows, user) => {
          const enrollment = enrollments.enrollments?.[user.adId ?? ''];
          if (enrollment?.hidden) return rows;
          const id = user.adId ?? '';
          if (seen.has(id)) return rows;
          seen.add(id);
          const status = enrollment?.status ?? '';
          rows.push({
            ...deriveMemberFields(user, status, enrollment?.dueDate),
            id,
            userId: user.adId,
            completionDate: enrollment?.completedDate,
            dueDate: enrollment?.dueDate,
            expirationDate: enrollment?.certificate?.expiration ?? '',
            finalScore: enrollment?.finalScore ?? 0,
            percentComplete: enrollment?.percentComplete ?? 0,
            locations: user.locationList,
            status,
            filterStatus: enrollment
              ? getFilterableStatus(status, !!enrollment.courseId)
              : 'not_completed',
            previouslyCompletedPlanAssignmentDate: '',
            timeSpentOnPlan: getTimeSpentOnPlan({
              duration: enrollment?.duration,
            }),
            completedStepCount: 0,
          });
          return rows;
        }, []),
      );
      return;
    }
  }, [activePlan, enrollments, planReport, setDataSource, users]);

  const getLocationsByPlan = usePlanLocations();

  // LearnUpon compliance plans don't have a Plan object (isPlanQueryEnabled is
  // false), so we fall back to the user's trainer+ locations.
  const filterLocations = activePlan?.plan
    ? getLocationsByPlan(activePlan.plan)
    : isCompliance
      ? (auth?.assignableTrainerLocations ?? [])
      : [];
  const canShowLocations = filterLocations.length;

  // Trigger print report modal with plan details
  const handlePrintReport = () => {
    if (activePlan?.plan) {
      printPlan(activePlan.plan);
      return;
    }
    // Reports route: getPlanAndAssignments is disabled (isPlanQueryEnabled
    // is always false when reportsRoute=true), so activePlan is undefined.
    // Use the plan data from the getPlanReport query instead.
    if (planReport) {
      printPlan({
        id: planReport.id,
        name: planReport.name ?? {},
        type: planReport.type ?? 'STORE',
        category: planReport.category,
        ownerId: planReport.ownerId,
        ownerType: planReport.ownerType,
      });
      return;
    }
    // LearnUpon compliance: both XP plan queries are disabled and only
    // enrollment data is available.
    printPlan({
      id: safePlanId,
      name: enrollments?.courseName ?? {},
      type: 'COMPLIANCE',
    });
  };

  useEffect(() => {
    if (shouldIRefetchAssignedCompliance) {
      refetchAssignedComplianceTeamMembers();
      toggle('refetchCompliance');
    }
  }, [
    toggle,
    refetchAssignedComplianceTeamMembers,
    shouldIRefetchAssignedCompliance,
  ]);

  useEffect(() => {
    if (shouldIRefetchAssignedTeamMembers && isValidPlanId) {
      refetchPlanAndAssignments();
      toggle('refetchAssignedTeamMembers');
    }
  }, [toggle, shouldIRefetchAssignedTeamMembers, planId, isValidPlanId]);

  // Clear Team Members Filters
  useEffect(() => {
    resetFilters();
    return () => {
      patchTrainingMode({ hasTeamMembersAssignedToPlan: false });
    };
  }, [patchTrainingMode, resetFilters]);

  useEffect(() => {
    if (isValidPlanId) {
      setActivePlanId(planId);
    }
  }, [planId, setActivePlanId, isValidPlanId]);

  const planAssignments =
    activePlan?.assignments ?? planReport?.planUserReports;
  useEffect(() => {
    if (planAssignments) {
      const allAssigneesCompleted = planAssignments
        .filter(it => it.status !== Constants.TRAINING_PLANS.UNASSIGNED)
        .every(user => user.status === Constants.TRAINING_PLANS.COMPLETED);
      patchTrainingMode({
        allAssigneesCompleted,
        hasTeamMembersAssignedToPlan: planAssignments.length > 0,
      });
    }
  }, [patchTrainingMode, planAssignments]);

  useEffect(() => {
    // We can't call `refetchAssignedTeamMembers` in compliance plans because we don't call `useGetAssignedStatusQuery` on component mount
    // Also guard against invalid planId since the query would be skipped
    if (isCompliance || !isValidPlanId || !state?.refetch) {
      return;
    }
    refetchPlanAndAssignments();
    // After we refetch the team members, we set the state back to false
    if (search) {
      navigate(
        { search },
        {
          replace: true,
          state: { refetch: false },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.refetch, isCompliance, isValidPlanId]);

  // GET Team Members By Location Fetch error
  if (isApiError(errorGettingUsers)) {
    datadogRum.addError(new Error(errorGettingUsers.message), {
      context: JSON.stringify(errorGettingUsers),
    });
    return <GenericError />;
  }

  if (isApiError(errorGettingComplianceStatus)) {
    datadogRum.addError(new Error(errorGettingComplianceStatus.message), {
      context: JSON.stringify(errorGettingComplianceStatus),
    });
    return <GenericError />;
  }

  // GET Assigned Team Members Fetch error
  if (isApiError(errorGettingPlan)) {
    datadogRum.addError(new Error(errorGettingPlan.message), {
      context: JSON.stringify(errorGettingPlan),
    });
    return <GenericError />;
  }

  const onAssignTeamMembersCompliance = (members: string | string[]) => {
    const payload = Array.isArray(members)
      ? { userIds: uniq(members), assignCourse: true as const }
      : { userIds: [members], assignCourse: true as const };

    const numberOfTeamMembersToAssign = payload.userIds.length;
    assignUsersToChecklist2({ body: payload, path: { courseId: safePlanId } })
      .then(async () => {
        if (!checkIsValidPlanId(safePlanId)) {
          throw new Error('Invalid planId');
        }
        queryClient.refetchQueries({
          queryKey: getActivePlanAssignmentsQueryKey(),
          type: 'all',
        });
        queryClient.refetchQueries({
          queryKey: listAssignedChecklistsQueryKey(),
          type: 'all',
        });
        queryClient.refetchQueries({
          queryKey: getPlanAndAssignmentsQueryKey({
            path: { planId: safePlanId },
          }),
          type: 'all',
        });
        queryClient.refetchQueries({
          queryKey: getManagedPlansQueryKey({ query: { status: 'ACTIVE' } }),
          type: 'all',
        });
        toggle('refetchCompliance');
        toggle('refetchCourseReport');
        showAssignmentSuccessToast(numberOfTeamMembersToAssign, t);
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const onAssignTeamMembers = (
    members: string | string[],
    dueDate: string | null,
  ) => {
    const dueDateToSend = dueDate as string | undefined;
    const userIds = Array.isArray(members) ? [...new Set(members)] : [members];
    assignTeamMembers({ planId: safePlanId, userIds, dueDate: dueDateToSend })
      .then(async () => {
        toggle('refetchAssignedTeamMembers');
        toggle('refetchAssignedChecklist');
        const count = userIds.length;
        await invalidateAllPlanQueries(safePlanId);
        invalidateCertificateQueries(safePlanId, userIds);
        showAssignmentSuccessToast(count, t);
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const onUpdateTeamMemberDueDate = (
    members: string | string[],
    dueDate: string | null,
  ) => {
    // null has to be passed here to clear the due date
    const dueDateToSend = dueDate as string | undefined;
    const payload = Array.isArray(members)
      ? { userIds: [...new Set(members)], dueDate: dueDateToSend }
      : { userIds: [members], dueDate: dueDateToSend };
    updateDueDate({ path: { planId: safePlanId }, body: payload })
      .then(() => {
        toggle('refetchAssignedChecklist');
        toggle('refetchAssignedTeamMembers');
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {t('TrainingPlans.toastMessage.updateDueDate', {
              teamMember: activeTeamMember.name,
            })}
          </ToastMessageBlock>
        ));
        onClose();
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const handleNextClick = ({
    isTrainingMode,
    selectedTeamMembers,
    id,
  }: {
    isTrainingMode: boolean;
    selectedTeamMembers: PathwayUser[];
    id?: string;
    autoAssign: boolean;
  }) => {
    setTemporaryTeamMembers(selectedTeamMembers);
    if (isTrainingMode) {
      sendTrainingModeAction('hideTrainingMode');
      setShowSelectLanguagePopup(true);
      setShowAddTeamMembersPopup(false);
    } else if (isLearnUpon && id) {
      setShowAddTeamMembersPopup(false);
      onAssignTeamMembersCompliance(id.includes(',') ? id.split(',') : id);
    } else if (isCompliance) {
      const offset = planAndAssignments?.plan?.dueDateOffsetInDays ?? 0;
      const withOffset = new Date();
      withOffset.setDate(withOffset.getDate() + offset);

      setShowAddTeamMembersPopup(false);
      onAssignTeamMembers(
        (id ?? '').includes(',') ? (id ?? '').split(',') : (id ?? ''),
        withOffset.toISOString(),
      );
    } else {
      setShowAddTeamMembersPopup(false);
      setSelectedId(id ?? '');
      setShowDueDatePopup(true);
    }
    patchTrainingMode({ showTrainTogetherPopup: false });
  };

  const onSaveDueDate = (dueDate: string | null) => {
    setShowDueDatePopup(false);
    if (isDueDateUpdate) {
      onUpdateTeamMemberDueDate(activeTeamMember.id, dueDate);
    } else {
      onAssignTeamMembers(
        selectedId.includes(',') ? selectedId.split(',') : selectedId,
        dueDate,
      );
      setTeamMembers(temporaryTeamMembers);
    }
  };

  const onAddTeamMember = () => {
    setShowAddTeamMembersMenu(false);
    setShowAddTeamMembersPopup(true);
  };

  const onUnassign = (id: string, name: string) => {
    setActiveTeamMember({ id, name });
    setShowUnassignTeamMemberPopup(true);
  };

  const onClose = () => {
    setShowAddTeamMembersMenu(false);
    setShowAddTeamMembersPopup(false);
    setShowDueDatePopup(false);
    setSelectedId('');
    patchTrainingMode({ showTrainTogetherPopup: false });
    setIsDueDateUpdate(false);
  };

  const onUpdateDueDate = (id: string, name: string) => {
    setIsDueDateUpdate(true);
    setActiveTeamMember({ id, name });
    setShowDueDatePopup(true);
  };

  const onEnterTrainingMode = () => {
    patchTrainingMode({
      showTrainTogetherPopup: true,
      isTrainingModeOpen: false,
    });
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    onOpenChange: open => {
      setFilterAndSortButtonIsOpen(open);
    },
    isOpen: filterAndSortButtonIsOpen,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      showing
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const locationOptions: FilterCheckboxSectionProps = {
    title: t('Generic.locations'),
    labels: filterLocations.reduce(
      (acc, loc) => ({
        ...acc,
        [loc]: {
          translationString: loc,
          value: currentFilters.includes(loc),
        },
      }),
      {},
    ),
    onToggle: v => toggleCheckboxFilter('locations', v),
  };

  const statusOptions: FilterCheckboxSectionProps = {
    title: t('Generic.status'),
    labels: statusLabels,
    onToggle: v => {
      toggleCheckboxFilter('filterStatus', v);
    },
  };

  const searchOptions: FilterSearchBarProps = {
    onChange: (e: React.ChangeEvent<TextFieldType>) =>
      setSearch(e.target.value, 'name'),
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.teamMembers.searchbarPlaceholder'),
    value: filters.search,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    before: planAndAssignments?.plan?.autoAssign ? (
      <MessageBlock.Root>
        {t('Generic.planIsAutoAssignedToAllNewHires')}
      </MessageBlock.Root>
    ) : undefined,
    search: <Filter.SearchBar {...searchOptions} />,
    children: (
      <>
        {!!canShowLocations && (
          <>
            <Filter.CheckboxSection {...locationOptions} />
            <Filter.Divider />
          </>
        )}
        <Filter.CheckboxSection {...statusOptions} />
      </>
    ),
  };

  if (!isValidPlanId && planId) {
    return <Navigate to={routes.plans.plans} replace />;
  }

  return (
    <>
      <Header.Root>
        <Header.Title>
          {itemByLanguage(planName) ?? itemByLanguage(enrollments?.courseName)}
        </Header.Title>
        <Header.Back
          to={reportsRoute ? routes.reports.plans : routes.plans.managedPlans}
          tx={
            reportsRoute
              ? 'Reports.tabPlans'
              : 'TrainingPlans.filtering.managePlans'
          }
        />
        {!reportsRoute && (
          <Header.ActionIconButton
            icon={IconUsersGroup}
            tooltip={
              !activePlan?.plan?.permissions?.trainTogether
                ? 'TrainingPlans.trainTogether.notAvailable'
                : isTrainTogetherEnabled
                  ? 'TrainingPlans.trainTogether.toolTip'
                  : 'TrainingPlans.trainTogether.toolTipDisabled'
            }
            text="Button.trainTogether"
            disabled={!isTrainTogetherEnabled}
            onClick={() => {
              track?.('showTrainingMode', {
                callSite: 'EditPlan',
                planId: safePlanId,
                isComplianceOrAnchor: isCompliance,
                locations: filterLocations,
              });
              sendTrainingModeAction('showTrainingMode');
            }}
          />
        )}
        {!reportsRoute && (
          <Header.ActionIconButton
            as="link"
            className={cn(
              'transition-opacity!',
              activePlan?.plan || enrollments?.courseName
                ? 'opacity-100!'
                : 'opacity-0!',
            )}
            to={routes.plans.managePlansEdit({ planId: safePlanId })}
            icon={activePlan?.plan?.readonly ? Show : IconEdit}
            tooltip={
              activePlan?.plan?.readonly
                ? 'Generic.viewPlanContent'
                : activePlan?.plan
                  ? 'Generic.editPlanContent'
                  : undefined
            }
            disabled={
              !safePlanId ||
              (activePlan?.plan?.permissions?.edit === false &&
                !activePlan.plan.readonly) ||
              !activePlan?.plan
            }
            onClick={e => {
              if (!activePlan?.plan?.readonly && hasTeamMembersAssignedToPlan) {
                e.preventDefault();
                setShowPlanInUseModal(true);
              }
            }}
          />
        )}
        {certificateHook.shouldShow && (
          <Header.ActionIconButton
            data-testid="certificate-icon-button"
            icon={IconCertificate}
            onClick={certificateHook.handleClick}
            tooltip="Button.getCertificates"
            disabled={certificateHook.isLoading}
          />
        )}
        <Header.ActionIconButton
          icon={IconPrinter}
          tooltip="Button.printReport"
          onClick={handlePrintReport}
        />
      </Header.Root>
      <LoadingOverlay isOpen={isFetching} fixed />
      <div className="md:mt-header-height-with-content-spacing-lg centered-content px-4 max-md:mt-6">
        <div className="flex max-w-full flex-col">
          {!isFetching && <FilterMobile {...filterOptions} />}
          {/* Top Row */}
          <div className="flex-end flex md:pl-[304px]">
            <SortFilterHeader
              label={t('TrainingPlans.filtering.sortBy')}
              onChange={setSort}
              onClear={() => {
                resetAllCheckboxFilters();
              }}
              options={sortOptions}
              showClear={false}
              showCompletedPlansOption={false}
              showMyCompletedPlansOnlySwitchValue={false}
              text={`${total} ${t('Generic.teamMembers')}`}
              title={
                reportsRoute
                  ? t('Generic.teamMembers')
                  : (itemByLanguage(planName) ??
                    itemByLanguage(enrollments?.courseName))
              }
              total={total}
              value={sortOptions.find(
                option =>
                  option.key === sort?.key &&
                  option.direction === sort?.direction,
              )}
              mobileRightSideAction={
                <PopoverMenu.Root
                  triggerButtonClassName="mr-2!"
                  panelProps={{ containerPadding: 25 }}
                >
                  {!reportsRoute && (
                    <>
                      <PopoverMenu.Action
                        icon={IconUserPlus}
                        tx="Generic.assign"
                        onClick={() => onAddTeamMember()}
                      />
                      <PopoverMenu.Action
                        as="link"
                        to={routes.plans.managePlansEdit({
                          planId: safePlanId,
                        })}
                        icon={activePlan?.plan?.readonly ? Show : IconEdit}
                        tx={
                          activePlan?.plan?.readonly
                            ? 'Generic.viewPlanContent'
                            : 'Generic.editPlanContent'
                        }
                        disabled={
                          !safePlanId ||
                          (activePlan?.plan?.permissions?.edit === false &&
                            !activePlan.plan.readonly) ||
                          !activePlan?.plan
                        }
                      />
                      <PopoverMenu.Action
                        icon={IconUsersGroup}
                        tx="Button.trainTogether"
                        onClick={() => {
                          track?.('showTrainingMode', {
                            callSite: 'EditPlan',
                            planId: safePlanId,
                            isComplianceOrAnchor: isCompliance,
                            locations: filterLocations,
                          });
                          sendTrainingModeAction('showTrainingMode');
                        }}
                        disabled={!isTrainTogetherEnabled}
                      />
                      <PopoverMenu.Divider />
                    </>
                  )}
                  {certificateHook.shouldShow && (
                    <PopoverMenu.Action
                      icon={IconCertificate}
                      tx="Button.getCertificates"
                      onClick={certificateHook.handleClick}
                      disabled={certificateHook.isLoading}
                      data-testid="certificate-button-mobile"
                    />
                  )}
                  <PopoverMenu.Action
                    icon={IconPrinter}
                    tx="Button.printReport"
                    onClick={handlePrintReport}
                  />
                  <PopoverMenu.Divider />
                  <PopoverMenu.Action
                    icon={IconFilter}
                    tx="TrainingPlans.filtering.filterAndSort"
                    onClick={() => setFilterAndSortButtonIsOpen(true)}
                  />
                </PopoverMenu.Root>
              }
            />
          </div>
          {/* Bottom Row */}
          <div className="mt-2.5 flex justify-between">
            {/* Left Side */}
            {!isFetching && <FilterDesktop {...filterOptions} />}
            {/* Right Side */}
            <div className="w-full">
              <TeamMembers
                currentFilters={currentFilters}
                filterLocations={filterLocations}
                isLearnUpon={isLearnUpon}
                isCompliance={isCompliance}
                isFoodSafety={isLearnUponFoodSafety}
                onUnassign={onUnassign}
                onUpdateDueDate={onUpdateDueDate}
                plan={planAndAssignments}
                planId={safePlanId}
                sort={sort?.key ?? ''}
                statusLabels={statusLabels}
                stepCount={
                  activePlan?.plan?.totalSteps ?? planReport?.totalSteps ?? 0
                }
                teamMembers={items}
                dataviewKey={dataViewKey}
                isReportsRoute={reportsRoute}
                userCanMarkAsComplete={
                  activePlan?.plan?.permissions?.markAllStepsAndPlanComplete
                }
                userAllowedToUpdateTMDueDateOnPlan={
                  userAllowedToUpdateTMDueDateOnPlan
                }
              />
            </div>
          </div>
        </div>
      </div>

      {!reportsRoute && (
        <>
          <AddTeamMemberFab
            onAddTeamMember={onAddTeamMember}
            setShowAddTeamMembersMenu={setShowAddTeamMembersMenu}
            showAddTeamMembersMenu={showAddTeamMembersMenu}
          />
          <EnterTrainTogether
            onEnterTrainingMode={onEnterTrainingMode}
            trainingModeModalIsOpen={trainingModeModalIsOpen}
          />
          {planAndAssignments?.plan && (
            <TrainTogether
              assignedTeamMembersList={{ status: activePlan?.assignments }}
              isOpen={showSelectLanguagePopup}
              onClose={() => setShowSelectLanguagePopup(false)}
              plan={planAndAssignments.plan}
              planId={safePlanId}
              temporaryTeamMembers={temporaryTeamMembers}
            />
          )}
          <UnassignTeamMember
            activeTeamMember={activeTeamMember}
            isCompliance={isLearnUpon}
            isOpen={showUnassignTeamMemberPopup}
            onClose={() => {
              setShowUnassignTeamMemberPopup(false);
              setActiveTeamMember({ id: '', name: '' });
            }}
            planId={safePlanId}
          />
          <DueDatePopUp
            isOpen={showDueDatePopup}
            onClose={onClose}
            onSave={date => onSaveDueDate(date)}
          />
          <AddTeamMembersPopUp
            handleNextClick={handleNextClick}
            isOpen={showAddTeamMembersPopup || showTrainTogetherPopup}
            isTrainingMode={showTrainTogetherPopup}
            onClose={onClose}
            plan={planAndAssignments?.plan}
            showAutoAssignToggle
            reassignEligible
            isCompliance={isCompliance}
          />
        </>
      )}

      <PrintCertificateModals {...certificateHook} />

      <PlanInUseModal
        isOpen={showPlanInUseModal}
        onClose={() => setShowPlanInUseModal(false)}
        onContinue={() => {
          setShowPlanInUseModal(false);
          navigate(routes.plans.managePlansEdit({ planId: safePlanId }));
        }}
      />
    </>
  );
};

const ManagePlansViewWithRoles = withRoles(PlanAssignees, 'plans.manage');

export default ManagePlansViewWithRoles;
